import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ImpactMetricsSection = () => {
  const sectionRef = useRef(null);

  const farmsRef = useRef(null);
  const wasteRef = useRef(null);
  const carbonRef = useRef(null);
  const distributorRef = useRef(null);

  useEffect(() => {
    const context = gsap.context(() => {
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content")
      );

      // 1. Scrubbed Text Reveal Animation
      textElements.forEach((element, index) => {
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element.closest("div"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });

        tl.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1,
        });
      });

      // 2. Divider Line Animation
      const lineElement = sectionRef.current.querySelector(
        ".about-divider-line"
      );
      if (lineElement) {
        gsap.set(lineElement, { scaleX: 0, transformOrigin: "left" });
        gsap.to(lineElement, {
          scaleX: 1,
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: lineElement.closest(".impact-subtitle-container"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }

      // 3. Counter Animation Logic
      const counterContainer = sectionRef.current.querySelector(
        ".counter-grid-container"
      );

      if (counterContainer) {
        const createCounter = (ref, target, formatFn) => {
          gsap.to(
            { count: 0 },
            {
              count: target,
              duration: 2.5,
              ease: "power1.out",
              onUpdate: function () {
                if (ref.current) {
                  ref.current.textContent = formatFn(
                    Math.ceil(this.targets()[0].count)
                  );
                }
              },
              scrollTrigger: {
                trigger: counterContainer,
                start: "top 80%",
                toggleActions: "play none none none",
              },
            }
          );
        };

        createCounter(farmsRef, 602, (v) => v.toLocaleString() + "K+");
        createCounter(
          wasteRef,
          170000,
          (v) => (v >= 1000 ? (v / 1000).toFixed(0) + "k" : v) + "+"
        );
        createCounter(carbonRef, 500, (v) => v + "k+");
        createCounter(distributorRef, 250, (v) => v + "+");
      }
    }, sectionRef);

    return () => context.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-[#f2f6ec] py-[25px] sm:py-[37px] 2xl:py-[50px] px-[32px] sm:px-[37px] 2xl:px-[50px]"
    >
      <div className="flex flex-col items-center w-full max-w-[1780px] m-auto">
        {/* Subtitle and Line */}
        <div className="flex flex-row justify-start items-center w-full impact-subtitle-container overflow-hidden mb-[30px] 2xl:mb-[40px]">
          <div className="overflow-hidden">
            <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[20px] sm:leading-[25px] 2xl:leading-[31px] text-left text-[#1c233a] font-[Sora] animated-text-content">
              Impact Metrics
            </p>
          </div>
          <div className="w-[100px] sm:w-[150px] 2xl:w-[200px] h-[1px] sm:h-[1.5px] 2xl:h-[2px] bg-[#1c233a] ml-[15px] sm:ml-[22px] 2xl:ml-[30px] mb-[7px] sm:mb-[10px] 2xl:mb-[14px] self-end about-divider-line"></div>
        </div>

        {/* Stats Cards Grid - Handles Responsiveness */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-[20px] sm:gap-[30px] 2xl:gap-[40px] w-full counter-grid-container">
          {/* Card 1 */}
          <div className="relative flex flex-col justify-between w-full h-[180px] sm:h-[219px] 2xl:h-[292px] bg-white rounded-[15px] md:rounded-[30px] p-[20px] sm:p-[15px] 2xl:p-[20px]">
            <div className="overflow-hidden">
              <p className="text-[18px] sm:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-[#1c233a] font-[Sora] w-[98%] animated-text-content">
                Kilograms of CO2 Emission Saved
              </p>
            </div>
            <div className="flex gap-[4px] sm:gap-[6px] 2xl:gap-[8px] justify-between items-start w-full h-auto">
              <div className="overflow-hidden">
                <h3
                  ref={farmsRef}
                  className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-extrabold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-[#1c233a] font-[Sora] animated-text-content"
                >
                  0+
                </h3>
              </div>
              <img
                src="/images/svg/ImpactMetrics1.svg"
                alt="CO2"
                className="object-contain w-[80px] sm:w-[70px] 2xl:w-auto"
              />
            </div>
          </div>

          {/* Card 2 */}
          <div className="relative flex flex-col justify-between w-full h-[180px] sm:h-[219px] 2xl:h-[292px] bg-white rounded-[15px] md:rounded-[30px] p-[20px] sm:p-[15px] 2xl:p-[20px]">
            <div className="overflow-hidden">
              <p className="text-[18px] sm:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-[#1c233a] font-[Sora] w-[98%] animated-text-content">
                Equivalent Tree Planting
              </p>
            </div>
            <div className="flex gap-[4px] sm:gap-[6px] 2xl:gap-[8px] justify-between items-start w-full h-auto">
              <div className="overflow-hidden">
                <h3
                  ref={wasteRef}
                  className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-extrabold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-[#1c233a] font-[Sora] animated-text-content"
                >
                  0k+
                </h3>
              </div>
              <img
                src="/images/svg/ImpactMetrics2.svg"
                alt="Trees"
                className="object-contain w-[80px] sm:w-[70px] 2xl:w-auto"
              />
            </div>
          </div>

          {/* Card 3 */}
          <div className="relative flex flex-col justify-between w-full h-[180px] sm:h-[219px] 2xl:h-[292px] bg-white rounded-[15px] md:rounded-[30px] p-[20px] sm:p-[15px] 2xl:p-[20px]">
            <div className="overflow-hidden">
              <p className="text-[18px] sm:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-[#1c233a] font-[Sora] w-[98%] animated-text-content">
                Farmers Transitioned - Organic Farming
              </p>
            </div>
            <div className="flex gap-[4px] sm:gap-[6px] 2xl:gap-[8px] justify-between items-start w-full h-auto">
              <div className="overflow-hidden">
                <h3
                  ref={carbonRef}
                  className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-extrabold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-[#1c233a] font-[Sora] animated-text-content"
                >
                  0%
                </h3>
              </div>
              <img
                src="/images/svg/ImpactMetrics3.svg"
                alt="Farmers"
                className="object-contain w-[80px] sm:w-[70px] 2xl:w-auto"
              />
            </div>
          </div>

          {/* Card 4 */}
          <div className="relative flex flex-col justify-between w-full h-[180px] sm:h-[219px] 2xl:h-[292px] bg-white rounded-[15px] md:rounded-[30px] p-[20px] sm:p-[15px] 2xl:p-[20px]">
            <div className="overflow-hidden">
              <p className="text-[18px] sm:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-[#1c233a] font-[Sora] w-[98%] animated-text-content">
                Distributor
              </p>
            </div>
            <div className="flex gap-[4px] sm:gap-[6px] 2xl:gap-[8px] justify-between items-start w-full h-auto">
              <div className="overflow-hidden">
                <h3
                  ref={distributorRef}
                  className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-extrabold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-[#1c233a] font-[Sora] animated-text-content"
                >
                  0%
                </h3>
              </div>
              <img
                src="/images/svg/ImpactMetrics4.svg"
                alt="Distributor"
                className="object-contain w-[80px] sm:w-[70px] 2xl:w-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactMetricsSection;
